import { FunctionComponent, useRef, useState } from "react";
import { Modal, TwoUp } from "@bluebottlecoffee/design-system/components";
import { AddSubscriptionItem } from "@chordcommerce/react-autonomy";
import { toTwoUpProps } from "../lib/transformers/two-up";
import {
  ChoiceQuizProjection,
  LastStepProjection,
  ProductQuizProjection,
  QuizProjection,
} from "../lib/sanity/quiz-queries";
import { QuizModal } from "./QuizModal";
import { ShippingCopy } from "../lib/sanity-schema";
import { ConversionCopy } from "../pages/[region]/[lang]/product/[slug]";
import { trackEvent } from "../lib/segment/track-event";

export type TrackingQuizFn = (
  step: string,
  choice?: string | null | Partial<AddSubscriptionItem & { name: string }>,
) => void;

export type SubscriptionQuizCopy = {
  buttonLoadingText: string;
  calculatorSliderFirstDetail: string;
  calculatorSliderLabel: string;
  calculatorSliderSecondDetail: string;
  weRecommend: string;
};

export type QuizSteps =
  ChoiceQuizProjection | ProductQuizProjection | LastStepProjection;

interface QuizProps extends QuizProjection {
  region: string;
  lang: string;
  shopcardCopy: {
    copy: ConversionCopy;
    shippingCopy: ShippingCopy;
  };
  subscriptionQuizCopy: SubscriptionQuizCopy;
}

export const Quiz: FunctionComponent<QuizProps> = ({
  start,
  valueProps,
  lang,
  introduction,
  region,
  shopcardCopy,
  subscriptionQuizCopy,
}) => {
  const [currentStep, setCurrentStep] = useState<QuizSteps>(undefined);
  const [history, setHistory] = useState<QuizSteps[]>([]);
  const id = introduction.anchorId ?? "subscription-quiz";
  const triggerRef = useRef<HTMLElement | null>(null);

  const handleNextStep = (nextStep: QuizSteps) => {
    setHistory([...history, currentStep]);
    setCurrentStep(nextStep);
  };

  const handlePrevStep = (onCloseModal: () => void) => {
    const prevStep = history.pop();
    setCurrentStep(prevStep);
    setHistory([...history]);
    if (!prevStep) onCloseModal();
  };

  const handleClose = () => {
    setCurrentStep(null);
    setHistory([]);
    triggerRef.current?.focus();
  };

  const trackingQuiz: TrackingQuizFn = (step, choice = null) => {
    trackEvent(id, {
      step,
      choice,
    });
  };

  return (
    <>
      <div id={id} />
      <TwoUp
        {...toTwoUpProps(introduction, region, lang, () => {
          triggerRef.current = document.activeElement as HTMLElement;
          handleNextStep(start);
          trackingQuiz("Get Started");
        })}
      />

      <Modal
        labelledById="" // Todo: add label
        modalRootId={id}
        onDismiss={handleClose}
        className="quiz-modal-wrapper"
        dismissButtonVisible={false}
        isOpen={Boolean(history.length && currentStep)}
        modalContentChildren={
          <QuizModal
            currentStep={currentStep}
            history={history}
            onClose={handleClose}
            onPrevStep={handlePrevStep}
            onNextStep={handleNextStep}
            valueProps={valueProps}
            lang={lang}
            region={region}
            shopcardCopy={shopcardCopy}
            subscriptionQuizCopy={subscriptionQuizCopy}
            trackingQuiz={trackingQuiz}
          />
        }
      />
    </>
  );
};
